import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Form, InputNumber, Tabs } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import { FeeCreatePayload } from "../../../types/feeTypes";
import { StudentType } from "types/studentTypes";
import { FeeType } from "types/feeTypeTypes";
import { formatMoney } from "helpers/numberHelper";
import CommonSelect, { Option as SelectOption } from "../../../components/Common/CommonSelect";
import CommonMultiSelect from "components/Common/CommonMultiSelect";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { getClassLevelLabelFromCode } from "utils/schoolUtils";
import { ClassData } from "types/classTypes";
import { ClassLevel } from "hooks/useReference";

interface AddEditFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FeeCreatePayload) => Promise<boolean>;
  students: StudentType[];
  feeTypes: FeeType[];
  classes: ClassData[];
  selectedStudent?: string;
  classLevels?: ClassLevel[];
}

const AddEditFeeModal: React.FC<AddEditFeeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  students,
  feeTypes,
  classes,
  selectedStudent,
  classLevels
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("student");

  const [selectedFeeType, setSelectedFeeType] = useState<FeeType | null>(null);
  const [selectedClassLevelId, setSelectedClassLevelId] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [selectedClassLevelTabId, setSelectedClassLevelTabId] = useState<string | null>(null);

  const findStudentById = useCallback(
    (id?: string | null) => (id ? students.find((s) => s.id === id) : undefined),
    [students]
  );

  const classLevelOptions = useMemo(
    () =>
      (classLevels || []).map((level: any) => ({
        label: getClassLevelLabelFromCode(level.labelKey),
        value: level.id,
      })),
    [classLevels]
  );

  const monthsOptions = useMemo(
    () => [
      { value: 0, label: t("list_of_months.January") },
      { value: 1, label: t("list_of_months.February") },
      { value: 2, label: t("list_of_months.March") },
      { value: 3, label: t("list_of_months.April") },
      { value: 4, label: t("list_of_months.May") },
      { value: 5, label: t("list_of_months.June") },
      { value: 6, label: t("list_of_months.July") },
      { value: 7, label: t("list_of_months.August") },
      { value: 8, label: t("list_of_months.September") },
      { value: 9, label: t("list_of_months.October") },
      { value: 10, label: t("list_of_months.November") },
      { value: 11, label: t("list_of_months.December") },
    ],
    [t]
  );

  const computeAmount = (feeType: FeeType | null, classLevelId?: string | null) => {
    if (!feeType) return undefined;

    if (feeType.pricingModel === "unique_payment") {
      return Number(feeType.baseAmount ?? 0);
    }

    if (feeType.pricingModel === "level_based_payment") {
      if (classLevelId) {
        const found = feeType.feePricing?.find((p) => p.classLevelId === classLevelId);
        if (found) return Number(found.amount ?? 0);
        return undefined;
      }
      return undefined;
    }

    return undefined;
  };

  // ---------- Handlers ----------
  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const { feePricingId, dueDate, dueMonths, ...rest } = values;

      const payload = {
        ...rest,
        ...(dueDate ? { dueDate: dueDate.format("MM-DD-YYYY") } : {}),
        ...(Array.isArray(dueMonths) ? { dueMonths } : {}),
      };

      const result = await onSave(payload);
      if (result) {
        onClose();
        form.resetFields();
        setSelectedFeeType(null);
        setSelectedClassLevelId(null);
        setSelectedClassId(null);
        setSelectedClassLevelTabId(null);
      }
    } catch (err) {
      console.error("Error saving fee:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (option: SelectOption | null) => {
    const newStudentId = option?.value || null;
    form.setFieldValue("studentId", newStudentId);

    const stu = findStudentById(newStudentId);

    const newClassLevelId = (stu as any)?.class?.classLevel?.id ?? null;
    setSelectedClassLevelId(newClassLevelId);

    const newAmount = computeAmount(selectedFeeType, newClassLevelId);
    form.setFieldsValue({ amount: newAmount });
  };

  const handleClassChange = (option: SelectOption | null) => {
    const classId = option?.value || null;
    form.setFieldValue("classId", classId);
    setSelectedClassId(classId);

    const klass = classes.find((c) => c.id === classId);
    const newClassLevelId = (klass as any)?.classLevel?.id ?? null;
    setSelectedClassLevelId(newClassLevelId);

    const newAmount = computeAmount(selectedFeeType, newClassLevelId);
    form.setFieldsValue({ amount: newAmount });

    if (classId) {
      const classStudents = students.filter((s) => s?.classId === classId);
      const ids = classStudents.map((s) => s.id);
      form.setFieldValue("studentIds", ids);
    } else {
      form.setFieldValue("studentIds", []);
    }
  };

  const handleClassStudentsChange = (ids: string[]) => {
    form.setFieldValue("studentIds", ids);
  };

  // ✅ NEW: class level tab handler
  const handleClassLevelChange = (option: SelectOption | null) => {
    const classLevelId = option?.value || null;

    form.setFieldValue("classLevelId", classLevelId);
    setSelectedClassLevelTabId(classLevelId);

    // important: for computeAmount, we also set selectedClassLevelId
    setSelectedClassLevelId(classLevelId);

    const newAmount = computeAmount(selectedFeeType, classLevelId);
    form.setFieldsValue({ amount: newAmount });

    if (classLevelId) {
      const levelStudents = students.filter(
        (s: any) => s?.class?.classLevel?.id === classLevelId
      );
      const ids = levelStudents.map((s) => s.id);
      form.setFieldValue("studentIds", ids);
    } else {
      form.setFieldValue("studentIds", []);
    }
  };

  const handleFeeTypeChange = (option: SelectOption | null) => {
    const feeTypeId = option?.value || null;
    const newFeeType = feeTypes.find((ft) => ft.id === feeTypeId) || null;

    setSelectedFeeType(newFeeType);
    form.setFieldValue("feeTypeId", feeTypeId);
    form.setFieldsValue({ feePricingId: undefined });

    const newAmount = computeAmount(newFeeType, selectedClassLevelId);
    form.setFieldsValue({ amount: newAmount });
  };

  const handleFeePricingChange = (option: SelectOption | null) => {
    if (!selectedFeeType) return;
    const selectedPricing = selectedFeeType.feePricing?.find((p) => p.id === option?.value);
    form.setFieldsValue({
      feePricingId: option?.value || null,
      amount: Number(selectedPricing?.amount ?? 0),
    });
  };

  // ---------- Effects ----------
  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedFeeType(null);
      setActiveTab("student");
      setSelectedClassId(null);
      setSelectedClassLevelTabId(null);

      if (selectedStudent) {
        form.setFieldValue("studentId", selectedStudent);
        const stu = findStudentById(selectedStudent);
        const lvl = (stu as any)?.class?.classLevel?.id ?? null;
        setSelectedClassLevelId(lvl);
      } else {
        setSelectedClassLevelId(null);
      }
    }
  }, [isOpen, selectedStudent, form, findStudentById]);

  useEffect(() => {
    const amount = computeAmount(selectedFeeType, selectedClassLevelId);
    if (amount !== undefined) form.setFieldsValue({ amount });
  }, [selectedFeeType, selectedClassLevelId, form]);

  // ---------- Options ----------
  const studentOptions: SelectOption[] = useMemo(
    () =>
      students.map((s) => ({
        value: s.id,
        label: `${s.user.firstName} ${s.user.lastName}`,
      })),
    [students]
  );

  const classOptions: SelectOption[] = useMemo(
    () =>
      classes.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [classes]
  );

  // ✅ class tab students
  const classStudentOptions: SelectOption[] = useMemo(() => {
    if (!selectedClassId) return [];
    const classStudents = students.filter((s) => s?.classId === selectedClassId);
    return classStudents.map((s) => ({
      value: s.id,
      label: `${s.user.firstName} ${s.user.lastName}`,
    }));
  }, [students, selectedClassId]);

  //  class level tab students
  const classLevelStudentOptions: SelectOption[] = useMemo(() => {
    if (!selectedClassLevelTabId) return [];
    const levelStudents = students.filter(
      (s: any) => s?.class?.classLevel?.id === selectedClassLevelTabId
    );
    return levelStudents.map((s) => ({
      value: s.id,
      label: `${s.user.firstName} ${s.user.lastName}`,
    }));
  }, [students, selectedClassLevelTabId]);

  const feeTypeOptions: SelectOption[] = useMemo(
    () => feeTypes.map((ft) => ({ value: ft.id, label: ft.name })),
    [feeTypes]
  );

  const feePricingOptions: SelectOption[] = useMemo(() => {
    if (!selectedFeeType?.feePricing) return [];
    return selectedFeeType.feePricing.map((p) => ({
      value: p.id,
      label: `${getClassLevelLabelFromCode(p.classLevel?.labelKey ?? "")} - ${formatMoney(
        Number(p.amount ?? 0)
      )}`,
    }));
  }, [selectedFeeType]);

  const items = [
    {
      key: "student",
      label: t("students_list"),
      children: (
        <Form.Item
          label={t("student")}
          name="studentId"
          rules={[
            { required: activeTab === "student", message: t("validation.please_select_student") },
          ]}
        >
          <CommonSelect
            options={studentOptions}
            minSearchChars={3}
            isSearchable
            onChange={handleStudentChange}
          />
        </Form.Item>
      ),
    },
    {
      key: "class",
      label: t("classes_list"),
      children: (
        <>
          <Form.Item
            label={t("class")}
            name="classId"
            rules={[
              { required: activeTab === "class", message: t("validation.please_select_class") },
            ]}
          >
            <CommonSelect options={classOptions} onChange={handleClassChange} />
          </Form.Item>

          {selectedClassId && (
            <Form.Item
              label={t("students_list")}
              name="studentIds"
              rules={[
                { required: activeTab === "class", message: t("validation.please_select_student") },
              ]}
            >
              <CommonMultiSelect
                options={classStudentOptions}
                placeholder={t("students_list")}
                value={form.getFieldValue("studentIds")}
                onChange={(ids: string[]) => handleClassStudentsChange(ids)}
              />
            </Form.Item>
          )}
        </>
      ),
    },
    // ✅ FIXED + NEW: classLevel tab
    {
      key: "classLevel",
      label: t("class_level"),
      children: (
        <>
          <Form.Item
            label={t("class_level")}
            name="classLevelId"
            rules={[
              {
                required: activeTab === "classLevel",
                message: t("validation.please_select_a_class_level"),
              },
            ]}
          >
            <CommonSelect options={classLevelOptions} onChange={handleClassLevelChange} />
          </Form.Item>

          {selectedClassLevelTabId && (
            <Form.Item
              label={t("students_list")}
              name="studentIds"
              rules={[
                {
                  required: activeTab === "classLevel",
                  message: t("validation.please_select_student"),
                },
              ]}
            >
              <CommonMultiSelect
                options={classLevelStudentOptions}
                placeholder={t("students_list")}
                value={form.getFieldValue("studentIds")}
                onChange={(ids: string[]) => handleClassStudentsChange(ids)}
              />
            </Form.Item>
          )}
        </>
      ),
    },
  ];

  const onChangeActiveTab = (tab: string) => {
    setActiveTab(tab);

    form.setFieldsValue({
      studentId: undefined,
      classId: undefined,
      classLevelId: undefined,
      studentIds: undefined,
    });

    form.resetFields([
      "studentId",
      "classId",
      "classLevelId",
      "studentIds",
      "feeTypeId",
      "feePricingId",
      "amount",
      "dueMonths",
      "dueDate",
    ]);

    setSelectedClassId(null);
    setSelectedClassLevelId(null);
    setSelectedClassLevelTabId(null);
  };

  return (
    <CustomModal
      title={t("add_fees")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={t("add")}
    >
      <Form form={form} layout="vertical">
        {selectedStudent ? (
          <Form.Item name="studentId" hidden initialValue={selectedStudent}>
            <input type="hidden" />
          </Form.Item>
        ) : (
          <Tabs activeKey={activeTab} onChange={onChangeActiveTab} items={items} />
        )}

        {/* Fee type */}
        <Form.Item
          label={t("fee_type")}
          name="feeTypeId"
          rules={[{ required: true, message: t("validation.please_select_fee_type") }]}
        >
          <CommonSelect options={feeTypeOptions} onChange={handleFeeTypeChange} />
        </Form.Item>

        {/* Pricing (only when level unknown) */}
        {selectedFeeType?.pricingModel === "level_based_payment" && !selectedClassLevelId && (
          <Form.Item
            label={t("pricing_type")}
            name="feePricingId"
            rules={[{ required: true, message: t("validation.please_select_fee_pricing") }]}
          >
            <CommonSelect options={feePricingOptions} onChange={handleFeePricingChange} />
          </Form.Item>
        )}

        {/* Amount */}
        <Form.Item
          label={t("amount")}
          name="amount"
          rules={[{ required: true, message: t("validation.please_enter_amount") }]}
        >
          <InputNumber
            className="w-100"
            placeholder={t("amount")}
            min={0}
            precision={2}
            addonAfter="MAD"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          />
        </Form.Item>

        {/* Unique vs recurring */}
        {selectedFeeType?.frequency === "unique_payment" ? (
          <Form.Item
            label={t("due_date")}
            name="dueDate"
            rules={[{ required: true, message: t("validation.please_select_due_date") }]}
          >
            <LocalizedDatePicker className="w-100" />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("select_months")}
            name="dueMonths"
            rules={[{ required: true, message: t("validation.please_select_months") }]}
          >
            <CommonMultiSelect
              options={monthsOptions}
              placeholder={t("select_months")}
              value={form.getFieldValue("dueMonths")}
              onChange={(value: number[]) => form.setFieldValue("dueMonths", value)}
            />
          </Form.Item>
        )}
      </Form>
    </CustomModal>
  );
};

export default AddEditFeeModal;
